import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BadgeCheck, MapPin, MessageSquare, CircleDot } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, fmtDateTime } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { MKT_CSS, MktHeader, Stars, ItemCard, useChatGate } from "./MktShared";
import { LoadingRows } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";

export default function FreelancerProfile() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [openChat, gateEl] = useChatGate();

  useEffect(() => {
    api.get(`/mkt/freelancers/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="min-h-screen bg-background"><style>{MKT_CSS}</style><MktHeader />
      <div className="max-w-xl mx-auto text-center py-24 px-6" data-testid="mkt-freelancer-404">
        <p className="font-display font-extrabold text-2xl">Freelancer not found</p>
        <Link to="/marketplace" className="inline-block mt-6 text-sm font-bold text-gradient">← Back to Marketplace</Link>
      </div>
    </div>);

  if (!data) return (
    <div className="min-h-screen bg-background"><style>{MKT_CSS}</style><MktHeader />
      <div className="max-w-7xl mx-auto px-6 py-12"><LoadingRows rows={4} /></div>
    </div>);

  const p = data.profile;

  return (
    <div className="min-h-screen bg-background">
      <style>{MKT_CSS}</style>
      <MktHeader />
      {gateEl}

      {/* PROFILE HERO */}
      <section className="mkt-hero relative overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14 text-white">
          <Link to="/marketplace" data-testid="mkt-profile-back" className="inline-flex items-center gap-1.5 text-xs font-bold text-white/70 hover:text-white transition-colors mb-6">
            <ArrowLeft className="h-3.5 w-3.5" /> Marketplace
          </Link>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
              {p.photo
                ? <img src={fileUrl(p.photo)} alt={p.display_name} className="h-24 w-24 rounded-3xl object-cover ring-4 ring-white/30" />
                : <div className="h-24 w-24 rounded-3xl bg-white/15 backdrop-blur flex items-center justify-center font-display font-extrabold text-4xl ring-4 ring-white/30">{(p.display_name || "F")[0]}</div>}
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="flex-1">
              <h1 data-testid="mkt-profile-name" className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight flex items-center gap-2">
                {p.display_name} {p.verified && <BadgeCheck className="h-6 w-6 text-sky-300" />}
              </h1>
              {p.title && <p className="text-white/85 mt-1">{p.title}</p>}
              <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-white/80">
                {p.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{p.location}</span>}
                <span className="flex items-center gap-1.5"><Stars rating={p.rating} /> {p.rating || 0} ({p.reviews || 0} reviews)</span>
                <span>{p.completed_orders || 0} orders completed</span>
                <span className="flex items-center gap-1"><CircleDot className={`h-3.5 w-3.5 ${p.availability === "available" ? "text-emerald-300" : "text-amber-300"}`} />{p.availability === "available" ? "Available now" : "Busy"}</span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(p.skills || []).map((s) => <span key={s} className="mkt-glass text-xs font-semibold px-3 py-1 rounded-full">{s}</span>)}
              </div>
            </motion.div>
            <Button data-testid="mkt-profile-contact"
              onClick={() => openChat(p.user_id, `chat with ${p.display_name}`)}
              className="rounded-full font-bold bg-white text-slate-900 hover:bg-white/90 active:scale-95 transition-transform shrink-0">
              <MessageSquare className="h-4 w-4 mr-1.5" /> Chat with Freelancer
            </Button>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-10">
        {p.about && (
          <div className="mkt-card p-6 max-w-3xl">
            <h2 className="font-display font-bold text-lg mb-2">About</h2>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed" data-testid="mkt-profile-about">{p.about}</p>
          </div>
        )}

        <Tabs defaultValue="services">
          <TabsList data-testid="mkt-profile-tabs" className="flex-wrap h-auto">
            <TabsTrigger value="services" data-testid="mkt-profile-tab-services">Services ({data.services.length})</TabsTrigger>
            <TabsTrigger value="products" data-testid="mkt-profile-tab-products">Products ({data.products.length})</TabsTrigger>
            <TabsTrigger value="courses" data-testid="mkt-profile-tab-courses">Courses ({data.courses.length})</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="mkt-profile-tab-reviews">Reviews ({data.reviews.length})</TabsTrigger>
          </TabsList>
          {["services", "products", "courses"].map((t) => (
            <TabsContent key={t} value={t} className="mt-6">
              {data[t].length === 0
                ? <p className="text-sm text-muted-foreground py-10 text-center">Nothing published here yet.</p>
                : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {data[t].map((it, i) => <ItemCard key={it.id} item={it} kind={t.slice(0, -1)} delay={i} />)}
                  </div>}
            </TabsContent>
          ))}
          <TabsContent value="reviews" className="mt-6">
            {data.reviews.length === 0
              ? <p className="text-sm text-muted-foreground py-10 text-center">No reviews yet.</p>
              : <div className="grid sm:grid-cols-2 gap-4 max-w-4xl">
                  {data.reviews.map((r) => (
                    <div key={r.id} data-testid={`mkt-review-${r.id}`} className="mkt-card p-5 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-sm">{r.buyer_name}</span>
                        <Stars rating={r.rating} />
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{r.text}</p>
                      <p className="text-[10px] text-muted-foreground/70">{fmtDateTime(r.created_at)} · Verified purchase</p>
                    </div>
                  ))}
                </div>}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
